import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config/environment';

/**
 * feat-163 — request a scorecard password reset (ADR 0001 §5).
 *
 * Anti-enumeration (AC2) is the crux of this feature, and it constrains this
 * component more than it looks. Two invariants live here:
 *
 *  1. This page NEVER calls Cognito's ForgotPassword directly. The prod client
 *     secret ships in this bundle (environment.ts), so SECRET_HASH is computed
 *     server-side and this page only ever talks to our backend. (ADR §9.)
 *  2. Where we navigate is NEVER derived from the request's outcome — not from
 *     the status, not from the body, not from whether the request even
 *     completed. Every submit lands on /reset-password with identical copy.
 *     There is no branch here to extend, which is the point: a branch is the
 *     mechanism by which a future edit leaks. (ADR §4.1-N, FINAL.)
 *
 * This component has no error state at all, deliberately. Nothing it could
 * learn about the request is safe to show, so there is nothing to render.
 */

/**
 * The one message shown for every server outcome. Deliberately a local constant
 * rather than the backend's `message` field: the backend returns an identical
 * string for every outcome by contract (ADR §4.1), but sourcing it from the
 * response would mean reading the response to decide what the user sees, and
 * the invariant that keeps AC2 from rotting is that this component never reads
 * it at all. A future edit that wants to vary this copy per-outcome has to
 * fight the code to do it, which is the point.
 */
const RESET_REQUESTED_MESSAGE =
  "If an account exists for that email, we've sent a reset code.";

const forgotPasswordUrl = (): string => {
  const scheme =
    config.environment === 'fake' || config.environment === 'local'
      ? 'http'
      : 'https';
  return `${scheme}://${config.restaurantBackendHost}/api/cognito-forgot-password`;
};

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // No AbortController and no client-side timeout here, deliberately.
      //
      // A timeout is a threshold on response time, and response time tracks
      // account state — QA measured a 33% median delta between an existing and
      // a nonexistent account (§4.2), so the timing channel is measured fact,
      // not theory. The nav rule below already denies a timeout anything to
      // change (it can only delay the same navigation), but we remove the
      // mechanism rather than rely on the absence of a branch.
      //
      // Do not add a timeout to this call. (ADR §5.4; found by feat-163-qa.)
      await fetch(forgotPasswordUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Swallowed on purpose. A transport failure is the one outcome that
      // provably carries no account signal — but reacting to it would still be
      // a branch on the request's outcome, and every future editor would have
      // to re-derive why that particular branch is safe before touching this
      // file. On the crux AC, mechanically-unbreakable beats
      // provably-correct-today. (ADR §4.1-N, FINAL.)
    }

    // Unconditional. Outside the try/catch, reachable by exactly one path, for
    // every outcome — 200, 400, 429, 500, DNS failure, offline. Nothing about
    // this call can change where the user goes or what they read.
    //
    // The cost this would otherwise carry — stranding a user who never gets a
    // code — is paid by the unconditional retry link on /reset-password
    // (§5.2-G2), not by a branch here.
    navigate('/reset-password', {
      state: { email, message: RESET_REQUESTED_MESSAGE },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bespoke AI</h1>
          <h2 className="text-xl text-gray-600 mb-8">Reset your password</h2>
        </div>
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <p className="text-sm text-gray-600 mb-6">
            Enter the email address for your account and we'll send you a reset
            code.
          </p>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send reset code'}
              </button>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/scorecard-login')}
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Back to sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
